'use client';

/** 시험안내 상단탭 컴포넌트 */

import { cn } from '@/lib/utils';

type TabItem = {
  id: string;
  label: string;
};

interface ExamNoticeTabsProps {
  title: string;
  firstDepthTabs: readonly TabItem[];
  secondDepthTabs?: readonly TabItem[];
  thirdDepthTabs?: readonly TabItem[];
  thirdDepthMode?: 'fit' | 'overflow';
  activeFirstDepth: string;
  activeSecondDepth?: string;
  activeThirdDepth?: string;
  onChangeFirstDepth: (id: string) => void;
  onChangeSecondDepth?: (id: string) => void;
  onChangeThirdDepth?: (id: string) => void;
}

function PillTabs({
  tabs,
  activeId,
  onChange,
  overflow,
  variant = 'default',
}: {
  tabs: readonly TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  overflow?: boolean;
  variant?: 'default' | 'secondDepth';
}) {
  return (
    <div
      className={cn(overflow ? 'overflow-x-auto hide-scrollbar' : '', 'w-full')}
    >
      <div
        className={cn(
          'flex gap-2',
          overflow ? 'w-max min-w-full px-5' : 'w-full flex-wrap',
        )}
      >
        {tabs.map((tab) => {
          const isActive = activeId === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={cn(
                'cursor-pointer',
                variant === 'secondDepth'
                  ? 'h-8 rounded-sm px-3 typo-14 font-semibold whitespace-nowrap'
                  : 'h-8 rounded-full px-3 typo-14 font-semibold whitespace-nowrap',
                isActive
                  ? variant === 'secondDepth'
                    ? 'bg-222222 text-white'
                    : 'bg-ffd42f text-333333 font-bold'
                  : variant === 'secondDepth'
                    ? 'bg-white border border-d6d4e2 text-[#908E9E]'
                    : 'bg-white border border-d6d4e2 text-[#4F4F5E]',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ExamNoticeTabs({
  title,
  firstDepthTabs,
  secondDepthTabs,
  thirdDepthTabs,
  thirdDepthMode,
  activeFirstDepth,
  activeSecondDepth,
  activeThirdDepth,
  onChangeFirstDepth,
  onChangeSecondDepth,
  onChangeThirdDepth,
}: ExamNoticeTabsProps) {
  return (
    <>
      <section className="sticky top-0 z-9 shrink-0 border-b-2 border-d6d4e2 bg-white h-[50px]">
        <div className="flex">
          {firstDepthTabs.map((tab) => {
            const isActive = activeFirstDepth === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChangeFirstDepth(tab.id)}
                className={cn(
                  'relative flex-1 h-[50px] typo-14 text-center cursor-pointer',
                  isActive
                    ? 'text-222222 font-extrabold'
                    : 'text-[#908E9E] font-semibold',
                )}
              >
                {tab.label}
                {isActive ? (
                  <span className="absolute bottom-0 left-1/2 h-[2px] w-full -translate-x-1/2 bg-[#908E9E]" />
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="px-5 pt-6 pb-4">
        <h2 className="typo-18 font-extrabold text-222222">{title}</h2>
      </section>

      {secondDepthTabs && secondDepthTabs.length > 0 && activeSecondDepth ? (
        <section className="px-5">
          <PillTabs
            tabs={secondDepthTabs}
            activeId={activeSecondDepth}
            onChange={(id) => onChangeSecondDepth?.(id)}
            variant="secondDepth"
          />
        </section>
      ) : null}

      {secondDepthTabs &&
      secondDepthTabs.length > 0 &&
      thirdDepthTabs &&
      thirdDepthTabs.length > 0 &&
      activeThirdDepth ? (
        <>
          {/* hr은 배경색 블록 밖에 두어, 구분선 위로는 회색(#F5F5FA)이 깔리지 않게 함 */}
          {thirdDepthMode === 'overflow' ? (
            <hr className="mb-0 mt-3 h-[2px] shrink-0 border-0 bg-d6d4e2" />
          ) : (
            <div className="px-5">
              <hr className="mb-0 mt-3 h-[2px] shrink-0 border-0 bg-d6d4e2 -mx-5" />
            </div>
          )}
          <section
            className={cn(
              'pb-3 pt-3',
              thirdDepthMode === 'overflow' ? 'bg-white' : 'bg-[#F5F5FA] px-5',
            )}
          >
            <PillTabs
              tabs={thirdDepthTabs}
              activeId={activeThirdDepth}
              onChange={(id) => onChangeThirdDepth?.(id)}
              overflow={thirdDepthMode === 'overflow'}
            />
          </section>
        </>
      ) : null}
    </>
  );
}
